import { Alert, AlertItem, Button } from '@vmware-react/clarity';
import React, { useCallback, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { AuthApi } from 'vrlcm-gui-api';
import { AsyncOpState, User } from 'vrlcm-gui-types';
import { ErrorResponse, toErrorMessage } from 'vrlcm-gui-utils';
import { SpinnerWithText } from '../../decorated-elements';
import { l10n } from '../i18n/i18nUtil';
import { InteractionBlocker } from '../../interaction-blocker';
import FirstBootPasswordForm, {
  FirstBootPasswordFormData
} from '../first-boot-password-update/FirstBootPasswordUpdateForm';
import './FirstBootPasswordUpdate.css';

type Props = { user: User } & RouteComponentProps;

const FormOverlayRenderer = (props: { size: 'sm' | 'md'; message: string }) => {
  const { size, message } = props;
  return (
    <div style={{ display: 'flex', justifyContent: 'center', height: '66%' }}>
      <SpinnerWithText size={size}>{message}</SpinnerWithText>
    </div>
  );
};

const FirstBootPasswordUpdate: React.FC<Props> = (props: Props) => {
  const { user } = props;
  const [submitState, setSubmitState] = useState<{
    state: AsyncOpState;
    errorMessage?: string;
  }>({
    state: AsyncOpState.NotStarted
  });

  const handleSubmit = useCallback(
    (formData: FirstBootPasswordFormData) => {
      setSubmitState({ state: AsyncOpState.Inprogress });
      AuthApi.updateFirstBootPassword(user.name, formData.password).then(
        () => {
          AuthApi.logout().then(
            () => setSubmitState({ state: AsyncOpState.Completed }),
            () => setSubmitState({ state: AsyncOpState.Completed })
          );
        },
        (err: ErrorResponse<{ message: string }>) => {
          setSubmitState({
            state: AsyncOpState.Failed,
            errorMessage: l10n(
              'components.withAppRoot.updatingPasswordFailed',
              [toErrorMessage(err)]
            )
          });
        }
      );
    },
    [user.name]
  );

  const disableFormInteraction = submitState.state === AsyncOpState.Inprogress;

  const overlaySpinnerMessage =
    submitState.state === AsyncOpState.Inprogress
      ? `${l10n('updating')}...`
      : '';

  const onFormDataChange = useCallback(() => {
    setSubmitState({ state: AsyncOpState.NotStarted });
  }, []);

  return (
    <div className="first-boot-password-update">
      {submitState.state !== AsyncOpState.Completed ? (
        <>
          <h3
            className="first-boot-password-update__header"
            dangerouslySetInnerHTML={{
              __html: l10n('components.withAppRoot.updatePassword', [user.name])
            }}
          ></h3>
          <Alert
            closable={true}
            ariaLiveMsg={l10n('components.withAppRoot.passwordCharacteristics')}
            items={[
              <AlertItem>
                {l10n('components.withAppRoot.passwordCharacteristics')}
              </AlertItem>
            ]}
          />
          <InteractionBlocker
            disableInteraction={disableFormInteraction}
            overlayRenderer={
              disableFormInteraction && (
                <FormOverlayRenderer
                  size="md"
                  message={overlaySpinnerMessage}
                />
              )
            }
          >
            <FirstBootPasswordForm
              onSubmit={handleSubmit}
              submitState={submitState.state}
              submitErrorMessage={submitState.errorMessage}
              onChange={onFormDataChange}
            />
          </InteractionBlocker>
        </>
      ) : (
        <div className="first-boot-password-update_success">
          <Alert
            type="success"
            ariaLiveMsg={l10n(
              'components.withAppRoot.passwordUpdateSuccessText',
              [user.name]
            )}
            items={[
              <AlertItem>
                <span
                  dangerouslySetInnerHTML={{
                    __html: l10n(
                      'components.withAppRoot.passwordUpdateSuccessHTML',
                      [user.name]
                    )
                  }}
                ></span>
              </AlertItem>
            ]}
          />
          <a href="/">
            <Button class="primary">
              <span style={{ marginRight: '3px' }}>{l10n('login')}</span>
              <clr-icon shape="arrow" dir="right" />
            </Button>
          </a>
        </div>
      )}
    </div>
  );
};

export default withRouter(FirstBootPasswordUpdate);
